// Calculs purs de la page Performance marchés. Toute règle ici est normée par
// PERFORMANCE_MARCHES_GROUND_TRUTH.md (références Dx entre parenthèses) : la lire avant de modifier.
import type { PriceFormula } from '../8-comparateur/utils/stats';

export type MarcheResultat = 'En attente' | 'Gagné' | 'Perdu';

// Champs utilisés par les calculs. Le type Marche de la page les étend.
export interface MarcheCalcul {
  resultat: MarcheResultat;
  tonPrix: number;
  prixGagnant?: number; // Prix de référence : prix de l'attributaire, sinon prix le plus bas estimé (D2)
  dateRemise?: string; // AAAA-MM-JJ (D11)
}

// Marché tel que saisi dans la page.
export interface Marche extends MarcheCalcul {
  id: string;
  nom: string;
  acheteur: string;
  ecart?: number; // En pourcentage
  notePrixObtenue?: number;
  prixAttributaire?: number; // Perdu : prix de l'offre retenue, communiqué ou publié (avis d'attribution, DECP)
  sourcePrixReference?: 'communique' | 'estime'; // Perdu : origine de prixGagnant (ground truth D2)
  notePrixMaximale?: number;
  modeNotation?: PriceFormula; // Mode de notation prix du marché (défaut: inverse)
}


// Seuils minimum de données (D10) : en dessous, la page affiche "Pas assez de données".
export const MIN_MARCHES_CLOTURES = 5; // indicateurs calculés sur tous les marchés clôturés
export const MIN_PERDUS = 3; // indicateurs calculés sur les seuls marchés perdus
export const MIN_MARCHES_PERIODE = 3; // tendance par semestre (D11)

// PCI d'un marché perdu en dessous duquel l'effort requis est jugé irréaliste (D9).
export const SEUIL_HORS_PORTEE = 80;

// Paliers du tableau "Rendement de l'effort", en % de la formule de prix habituelle (D6).
export const PALIERS_EFFORT = [3, 5, 8, 12];

// Précision de comparaison entre effort simulé et effort requis, en points de % (D5).
export const TOLERANCE_EFFORT = 0.05;

// Reconstruit le prix le plus bas de la consultation à partir de ta note prix (D2).
// - inverse et subtractive s'inversent exactement à partir de (tonPrix, note, échelle).
// - linear dépend aussi du prix le plus cher, inconnu ici : on retombe sur l'approximation inverse.
export function estimerPrixGagnant(
  tonPrix: number,
  notePrixObtenue: number,
  notePrixMaximale: number,
  mode: PriceFormula
): number | undefined {
  if (notePrixMaximale <= 0) return undefined;
  const ratio = notePrixObtenue / notePrixMaximale;
  if (mode === 'subtractive') {
    const denom = 2 - ratio;
    return denom > 0 ? tonPrix / denom : undefined;
  }
  return ratio > 0 ? tonPrix * ratio : undefined;
}

// Perdu alors qu'on était au prix de référence ou en dessous : le prix n'était pas le problème (D3).
export const estPerduHorsPrix = (m: MarcheCalcul): boolean =>
  m.resultat === 'Perdu' && m.prixGagnant !== undefined && m.prixGagnant >= m.tonPrix;

// Score PCI d'un marché clôturé (D4) : 100 si gagné ou perdu hors prix, sinon prix de référence
// ÷ ton prix × 100. null si en attente ou perdu sans prix de référence connu.
export const scorePci = (m: MarcheCalcul): number | null => {
  if (m.resultat === 'Gagné') return 100;
  if (m.resultat !== 'Perdu' || !m.prixGagnant || m.prixGagnant <= 0 || m.tonPrix <= 0) return null;
  return Math.min(100, (m.prixGagnant / m.tonPrix) * 100);
};

// PCI d'un marché perdu hors "hors prix" (colonne PCI de la liste). null sinon.
export const pciPerdu = (m: MarcheCalcul): number | null => {
  if (m.resultat !== 'Perdu' || !m.prixGagnant || m.prixGagnant <= 0 || m.tonPrix <= 0 || estPerduHorsPrix(m)) {
    return null;
  }
  return (m.prixGagnant / m.tonPrix) * 100;
};

// Lecture du PCI global (D4).
export const labelPciGlobal = (v: number): string => (v >= 95 ? 'Compétitif' : v >= 85 ? 'Dans le marché' : 'Hors marché');

export const mediane = (valeurs: number[]): number | null => {
  if (valeurs.length === 0) return null;
  const tri = [...valeurs].sort((a, b) => a - b);
  const milieu = Math.floor(tri.length / 2);
  return tri.length % 2 ? tri[milieu] : (tri[milieu - 1] + tri[milieu]) / 2;
};

// Semestre de remise (D11).
export const periodeDe = (dateRemise: string): { cle: string; label: string } => {
  const [annee, mois] = dateRemise.split('-').map(Number);
  const semestre = mois <= 6 ? 1 : 2;
  return { cle: `${annee}-S${semestre}`, label: `S${semestre} ${String(annee).slice(2)}` };
};

// Indicateurs du cockpit (D4, D9) : taux de succès, PCI global, PCI des perdus, marge de progression.
export function calculerKpis(marches: MarcheCalcul[]) {
  const clotures = marches.filter(m => m.resultat !== 'En attente');
  const gagnes = marches.filter(m => m.resultat === 'Gagné');
  const perdus = marches.filter(m => m.resultat === 'Perdu');

  const tauxSucces = clotures.length > 0 ? (gagnes.length / clotures.length) * 100 : 0;

  // Perdus avec un prix de référence, hors "hors prix" (D3).
  const perdusAvecReference = perdus.filter(m => m.prixGagnant !== undefined && m.prixGagnant > 0 && !estPerduHorsPrix(m));
  const nombrePerdusHorsPrix = perdus.filter(estPerduHorsPrix).length;

  // Marge de progression : effort requis en % de TON prix, médiane sur les perdus à portée (D9).
  const effortsPerdus = perdusAvecReference.map(m => ({
    pci: (m.prixGagnant! / m.tonPrix) * 100,
    effort: ((m.tonPrix - m.prixGagnant!) / m.tonPrix) * 100
  }));
  const perdusAPortee = effortsPerdus.filter(e => e.pci >= SEUIL_HORS_PORTEE);

  const pciMoyen = effortsPerdus.length > 0 ? effortsPerdus.reduce((s, e) => s + e.pci, 0) / effortsPerdus.length : 0;

  // PCI global : 100 est un plancher pour les gagnés (leur vraie avance est inconnue, C2).
  const scores = clotures.map(scorePci).filter((v): v is number => v !== null);
  const pciGlobal = scores.length > 0 ? scores.reduce((a, b) => a + b, 0) / scores.length : null;

  return {
    tauxSucces,
    nombreMarchesGagnes: gagnes.length,
    nombreMarchesFermes: clotures.length,
    effortMedianAPortee: mediane(perdusAPortee.map(e => e.effort)),
    nombrePerdusAvecReference: perdusAvecReference.length,
    nombrePerdusAPortee: perdusAPortee.length,
    nombrePerdusHorsPortee: effortsPerdus.length - perdusAPortee.length,
    nombrePerdusHorsPrix,
    pciMoyen,
    pciGlobal,
    nombreMarchesPciGlobal: scores.length
  };
}

// Évolution du PCI global par semestre de remise (D11). Les marchés sans date sont exclus. La
// tendance compare les deux dernières périodes ayant au moins MIN_MARCHES_PERIODE marchés.
export function calculerTendancePci(marches: MarcheCalcul[]) {
  const parPeriode = new Map<string, { label: string; scores: number[] }>();
  marches.forEach(m => {
    const score = scorePci(m);
    if (score === null || !m.dateRemise) return;
    const { cle, label } = periodeDe(m.dateRemise);
    const entree = parPeriode.get(cle) ?? { label, scores: [] };
    entree.scores.push(score);
    parPeriode.set(cle, entree);
  });
  const historique = Array.from(parPeriode.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([cle, { label, scores }]) => ({
      cle,
      label,
      n: scores.length,
      pci: scores.reduce((sum, v) => sum + v, 0) / scores.length
    }));
  if (historique.length < 2) return null;

  const comparables = historique.filter(h => h.n >= MIN_MARCHES_PERIODE);
  const derniere = comparables.length >= 2 ? comparables[comparables.length - 1] : null;
  const precedente = comparables.length >= 2 ? comparables[comparables.length - 2] : null;
  return { historique, derniere, precedente };
}

// Effort requis par marché perdu (hors "hors prix"), en % de ton prix, trié du plus facile au plus dur.
export function calculerSeuilsReduction<T extends MarcheCalcul>(marches: T[]) {
  return marches
    .filter((m): m is T & { prixGagnant: number } => m.resultat === 'Perdu' && m.prixGagnant !== undefined && m.prixGagnant < m.tonPrix)
    .map(m => ({ marche: m, reductionSeuil: ((m.tonPrix - m.prixGagnant) / m.tonPrix) * 100 }))
    .sort((a, b) => a.reductionSeuil - b.reductionSeuil);
}

// Baisse de prix obtenue en combinant les deux leviers, en % du prix habituel (D5, D8).
// Positive = baisse. margeBase et margeSimulee en %, reductionCout en % du coût.
export const calculerEffortSimule = (margeBase: number, margeSimulee: number, reductionCout: number): number =>
  (1 - ((1 - reductionCout / 100) * (1 + margeSimulee / 100)) / (1 + margeBase / 100)) * 100;

// Marchés perdus dont l'effort requis ne dépasse pas l'effort donné (D5).
export function marchesAPorteeDeLEffort<T extends MarcheCalcul>(
  seuils: { marche: T; reductionSeuil: number }[],
  effort: number
): T[] {
  return seuils.filter(s => s.reductionSeuil <= effort + TOLERANCE_EFFORT).map(s => s.marche);
}

export interface LigneRendement {
  palier: number;
  tauxSucces: number;
  marchesEnPlus: number;
  netLevierMarge: number;
  netLevierCout: number;
}

// Rendement de l'effort (D6) : pour chaque palier, la formule de prix baisse de x % sur TOUS les
// marchés. Les gagnés restent gagnés mais perdent x % de prix ; les perdus à portée sont récupérés.
// - levier marge : coût inchangé, la baisse est prise sur la marge ;
// - levier coût : coût baissé de x %, taux de marge conservé.
// null si marge de base non renseignée ou moins de MIN_MARCHES_CLOTURES clôturés (D10).
export function calculerRendementEffort(marches: MarcheCalcul[], margeBase: number): LigneRendement[] | null {
  if (margeBase <= 0) return null;
  const nbClotures = marches.filter(m => m.resultat !== 'En attente').length;
  if (nbClotures < MIN_MARCHES_CLOTURES) return null;

  const m = margeBase / 100;
  const partMarge = m / (1 + m);
  const gagnes = marches.filter(mc => mc.resultat === 'Gagné');
  const caGagne = gagnes.reduce((sum, mc) => sum + mc.tonPrix, 0);
  const seuils = calculerSeuilsReduction(marches);

  return PALIERS_EFFORT.map(palier => {
    const x = palier / 100;
    const recuperes = marchesAPorteeDeLEffort(seuils, palier);
    const caRecupere = recuperes.reduce((sum, mc) => sum + mc.tonPrix, 0);
    return {
      palier,
      tauxSucces: ((gagnes.length + recuperes.length) / nbClotures) * 100,
      marchesEnPlus: recuperes.length,
      netLevierMarge: caRecupere * (1 - x - 1 / (1 + m)) - x * caGagne,
      netLevierCout: caRecupere * (1 - x) * partMarge - x * caGagne * partMarge
    };
  });
}

// Recommandation (D6, D9) : palier au meilleur résultat net positif, levier marge d'abord (sans
// effort opérationnel), sinon levier coût, sinon aucun (garder la formule actuelle).
export function choisirRecommandation(
  rendement: LigneRendement[]
): { levier: 'marge' | 'cout'; ligne: LigneRendement } | { levier: null; ligne: null } {
  const meilleur = (cle: 'netLevierMarge' | 'netLevierCout') =>
    [...rendement].filter(r => r.marchesEnPlus > 0 && r[cle] > 0).sort((a, b) => b[cle] - a[cle])[0];
  const parMarge = meilleur('netLevierMarge');
  if (parMarge) return { levier: 'marge', ligne: parMarge };
  const parCout = meilleur('netLevierCout');
  if (parCout) return { levier: 'cout', ligne: parCout };
  return { levier: null, ligne: null };
}

// Plafond du levier "réduction de coût" du simulateur, en % du coût (D8, D9). Aligné sur le seuil
// hors de portée : au-delà de 20 %, ce n'est plus un réglage de prix.
export const REDUCTION_COUT_MAX = 100 - SEUIL_HORS_PORTEE;

// Simulateur "Pilotage de la marge" (D5, D7, D8, D9).
// - Baisse uniquement (D7) : la marge simulée est bornée à [0, marge de base], la réduction de
//   coût à [0, REDUCTION_COUT_MAX]. Aucune hausse de prix n'est simulée.
// - Les gagnés restent gagnés ; les perdus à portée (PCI ≥ SEUIL_HORS_PORTEE) dont l'effort requis
//   ne dépasse pas l'effort simulé sont récupérés. Les hors de portée et hors prix sont exclus.
// - CA et marge : même modèle que le rendement de l'effort (coût = ton prix ÷ (1 + marge de base)).
// null si la marge de base n'est pas renseignée ou s'il y a moins de MIN_MARCHES_CLOTURES clôturés (D10).
export function simulerPilotage<T extends MarcheCalcul>(
  marches: T[],
  margeBase: number,
  margeSimulee: number,
  reductionCout: number
) {
  if (margeBase <= 0) return null;
  if (marches.filter(m => m.resultat !== 'En attente').length < MIN_MARCHES_CLOTURES) return null;

  const marge = Math.min(Math.max(margeSimulee, 0), margeBase);
  const reduction = Math.min(Math.max(reductionCout, 0), REDUCTION_COUT_MAX);
  const facteurBase = 1 + margeBase / 100;
  const facteurSimule = 1 + marge / 100;
  const coutBase = (m: T) => m.tonPrix / facteurBase;
  const coutSimule = (m: T) => coutBase(m) * (1 - reduction / 100);

  const gagnes = marches.filter(m => m.resultat === 'Gagné');
  const seuils = calculerSeuilsReduction(marches);
  const seuilsAPortee = seuils.filter(s => s.reductionSeuil <= 100 - SEUIL_HORS_PORTEE + TOLERANCE_EFFORT);
  const effortSimule = calculerEffortSimule(margeBase, marge, reduction);
  const recuperes = marchesAPorteeDeLEffort(seuilsAPortee, effortSimule);
  const ensemble = [...gagnes, ...recuperes];

  const margeActuelle = gagnes.reduce((s, m) => s + coutBase(m) * (facteurBase - 1), 0);
  const margeSimuleeEur = ensemble.reduce((s, m) => s + coutSimule(m) * (facteurSimule - 1), 0);

  return {
    effortSimule,
    nombreGagnesActuel: gagnes.length,
    nombreGagnesSimule: ensemble.length,
    recuperes,
    seuilsAPortee,
    nombreHorsPortee: seuils.length - seuilsAPortee.length,
    caActuel: gagnes.reduce((s, m) => s + m.tonPrix, 0),
    caSimule: ensemble.reduce((s, m) => s + coutSimule(m) * facteurSimule, 0),
    margeActuelle,
    margeSimulee: margeSimuleeEur
  };
}

// Comment l'utilisateur finance une baisse de prix (D8) :
// - 'marge' : le coût ne bouge pas, la baisse est prise sur la marge ;
// - 'cout' : la marge (en %) est conservée, le coût baisse d'autant ;
// - 'mix' : le coût d'abord (jusqu'à REDUCTION_COUT_MAX), puis la marge.
export type ModeFinancement = 'marge' | 'cout' | 'mix';

// Baisse de prix maximale possible avec un mode donné, en % : au-delà, la marge deviendrait
// négative ou la réduction de coût dépasserait son plafond.
export function baisseMax(margeBase: number, mode: ModeFinancement): number {
  if (margeBase <= 0) return 0;
  const facteurBase = 1 + margeBase / 100;
  if (mode === 'marge') return (1 - 1 / facteurBase) * 100;
  if (mode === 'cout') return REDUCTION_COUT_MAX;
  return (1 - (1 - REDUCTION_COUT_MAX / 100) / facteurBase) * 100;
}

// Traduit une baisse de prix (en %) et un mode de financement en réglage des deux leviers.
// La baisse est bornée à [0, baisseMax] : jamais de hausse (D7).
export function repartirBaisse(
  margeBase: number,
  baisse: number,
  mode: ModeFinancement
): { margeSimulee: number; reductionCout: number; baisse: number } {
  const x = Math.min(Math.max(baisse, 0), baisseMax(margeBase, mode)) / 100;
  const facteurBase = 1 + margeBase / 100;
  const reduction = mode === 'marge' ? 0 : mode === 'cout' ? x : Math.min(x, REDUCTION_COUT_MAX / 100);
  const margeSimulee = (facteurBase * (1 - x) / (1 - reduction) - 1) * 100;
  return { margeSimulee: Math.max(0, margeSimulee), reductionCout: reduction * 100, baisse: x * 100 };
}

// Marge sur coût après une baisse de prix prise entièrement sur la marge (D9), en %.
// Ex. marge 15 %, baisse de prix 3 % → 1,15 × 0,97 − 1 = 11,55 %.
export const margeApresBaisse = (margeBase: number, baisse: number): number =>
  ((1 + margeBase / 100) * (1 - baisse / 100) - 1) * 100;

// Période d'analyse du cockpit (D16) : 12 ou 24 derniers mois de remise, ou tout l'historique.
export type PeriodeAnalyse = '12m' | '24m' | 'tous';
export const MOIS_PERIODE: Record<Exclude<PeriodeAnalyse, 'tous'>, number> = { '12m': 12, '24m': 24 };

// Marchés dont la date de remise tombe dans la période (D16). Hors "tous", un marché sans date
// de remise est exclu (D11 : jamais l'ordre de saisie comme proxy).
export function filtrerParPeriode<T extends MarcheCalcul>(marches: T[], periode: PeriodeAnalyse, aujourdhui: Date = new Date()): T[] {
  if (periode === 'tous') return marches;
  const debut = new Date(aujourdhui.getFullYear(), aujourdhui.getMonth() - MOIS_PERIODE[periode], aujourdhui.getDate());
  const pad = (n: number) => String(n).padStart(2, '0');
  const debutIso = `${debut.getFullYear()}-${pad(debut.getMonth() + 1)}-${pad(debut.getDate())}`;
  return marches.filter(m => m.dateRemise !== undefined && m.dateRemise >= debutIso);
}

// Robustesse de la recommandation (D17). Une recommandation de baisse est "fragile" si :
// - elle ne récupère qu'un seul marché ('un-seul-marche') ;
// - ou si elle change (levier, palier, ou plus de baisse) quand on retire le plus gros marché
//   récupéré, en CA ('depend-plus-gros') : le résultat net est pesé en euros, un gros lot peut
//   la porter à lui seul (C3).
// null si pas de recommandation de baisse (rendement indisponible ou "gardez votre formule").
export function evaluerRobustesse<T extends MarcheCalcul>(
  marches: T[],
  margeBase: number
): {
  fragile: boolean;
  raison: 'un-seul-marche' | 'depend-plus-gros' | null;
  marcheDecisif: T; // plus gros marché récupéré (en CA) au palier recommandé
  sansLui: { levier: 'marge' | 'cout'; palier: number } | null; // recommandation sans ce marché (null : gardez votre formule)
} | null {
  const rendement = calculerRendementEffort(marches, margeBase);
  if (!rendement) return null;
  const choix = choisirRecommandation(rendement);
  if (!choix.levier) return null;

  const recuperes = marchesAPorteeDeLEffort(calculerSeuilsReduction(marches), choix.ligne.palier);
  const plusGros = recuperes.reduce((a, b) => (b.tonPrix > a.tonPrix ? b : a));
  const rendementSans = calculerRendementEffort(marches.filter(m => m !== plusGros), margeBase);
  const choixSans = rendementSans ? choisirRecommandation(rendementSans) : { levier: null, ligne: null };
  const sansLui = choixSans.levier ? { levier: choixSans.levier, palier: choixSans.ligne.palier } : null;
  if (recuperes.length === 1) return { fragile: true, raison: 'un-seul-marche', marcheDecisif: plusGros, sansLui };
  const change = sansLui?.levier !== choix.levier || sansLui?.palier !== choix.ligne.palier;
  return { fragile: change, raison: change ? 'depend-plus-gros' : null, marcheDecisif: plusGros, sansLui };
}
